'use client';

import { forwardRef, type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

type ButtonVariant =
  | 'primary'
  | 'primary-outline'
  | 'secondary'
  | 'tertiary'
  | 'danger'
  | 'danger-outline';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-white shadow-primary-btn hover:opacity-90',
  'primary-outline':
    'bg-transparent text-primary border border-primary hover:bg-primary/10',
  secondary:
    'bg-surface-secondary text-ink-secondary hover:bg-surface-container-high',
  tertiary:
    'bg-transparent text-primary hover:bg-surface-container-low',
  danger: 'bg-error text-white hover:opacity-90',
  'danger-outline':
    'bg-transparent text-error border border-error hover:bg-error/10',
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-5 py-2.5 text-sm',
  lg: 'px-7 py-3 text-base',
};

function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        // WCAG 4.1.3 (F5): expose loading state to AT via `aria-busy` and
        // `aria-live="polite"` on the spinner host so screen readers announce
        // the transition into / out of the loading state for the button.
        aria-busy={loading || undefined}
        aria-live={loading ? 'polite' : undefined}
        className={clsx(
          'inline-flex items-center justify-center gap-2 rounded-[12px] font-ui font-medium transition-all',
          // WCAG 2.4.7 / 1.4.11: keyboard-visible focus ring on every Button.
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          // Tactile pressed feedback (a button that doesn't depress feels cheap);
          // suppressed under prefers-reduced-motion.
          'motion-safe:active:scale-[0.98]',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {loading && (
          <>
            <Spinner />
            {/* Hidden text so AT announces "Loading" while the button is busy. */}
            <span className="sr-only">Loading</span>
          </>
        )}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button };
export type { ButtonProps, ButtonVariant, ButtonSize };
